import React, { useState, useEffect } from 'react';
import type { User, CreateUserInput, UpdateUserInput } from '@lnr-v2/types';
import {
  USER_ROLE_LOWERCASE,
  USER_STATUS_LOWERCASE,
  ROLE_DESCRIPTIONS_LOWERCASE,
  VALIDATION_MESSAGES,
} from '@lnr-v2/types';
import { Modal, Button, Input } from '@lnr-v2/ui';
import styles from './UserForm.module.css';

export interface UserFormProps {
  user?: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateUserInput | UpdateUserInput) => Promise<void>;
}

export const UserForm: React.FC<UserFormProps> = ({
  user,
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    username: '',
    role: USER_ROLE_LOWERCASE.VIEWER as
      | typeof USER_ROLE_LOWERCASE.SUPER_ADMIN
      | typeof USER_ROLE_LOWERCASE.ADMIN
      | typeof USER_ROLE_LOWERCASE.EDITOR
      | typeof USER_ROLE_LOWERCASE.VIEWER,
    status: USER_STATUS_LOWERCASE.ACTIVE as
      | typeof USER_STATUS_LOWERCASE.ACTIVE
      | typeof USER_STATUS_LOWERCASE.INACTIVE
      | typeof USER_STATUS_LOWERCASE.PENDING
      | typeof USER_STATUS_LOWERCASE.SUSPENDED,
    avatar: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when user changes or modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (user) {
        setFormData({
          name: user.name,
          email: user.email,
          password: '',
          username: '',
          role: user.role,
          status: user.status,
          avatar: user.avatar || '',
        });
      } else {
        setFormData({
          name: '',
          email: '',
          password: '',
          username: '',
          role: USER_ROLE_LOWERCASE.VIEWER,
          status: USER_STATUS_LOWERCASE.ACTIVE,
          avatar: '',
        });
      }
      setErrors({});
    }
  }, [user, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = VALIDATION_MESSAGES.NAME_REQUIRED;
    }

    if (!formData.email.trim()) {
      newErrors.email = VALIDATION_MESSAGES.EMAIL_REQUIRED;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = VALIDATION_MESSAGES.INVALID_EMAIL;
    }

    // Username validation (optional but must be valid if provided)
    if (formData.username.trim()) {
      if (!/^[a-zA-Z0-9_-]{3,20}$/.test(formData.username)) {
        newErrors.username = VALIDATION_MESSAGES.INVALID_USERNAME;
      }
    }

    // Password validation (only required when creating new user)
    if (!user) {
      if (!formData.password) {
        newErrors.password = VALIDATION_MESSAGES.PASSWORD_REQUIRED;
      } else if (formData.password.length < 8) {
        newErrors.password = VALIDATION_MESSAGES.PASSWORD_TOO_SHORT;
      } else {
        const passwordErrors: string[] = [];

        if (!/[A-Z]/.test(formData.password)) {
          passwordErrors.push('one uppercase letter');
        }
        if (!/[a-z]/.test(formData.password)) {
          passwordErrors.push('one lowercase letter');
        }
        if (!/[0-9]/.test(formData.password)) {
          passwordErrors.push('one number');
        }
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
          passwordErrors.push('one special character (!@#$%^&*(),.?":{}|<>)');
        }

        if (passwordErrors.length > 0) {
          newErrors.password = `Password must contain at least ${passwordErrors.join(', ')}`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const data = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        ...(formData.password && !user && { password: formData.password }),
        ...(formData.username.trim() && { username: formData.username.trim() }),
        role: formData.role,
        status: formData.status,
        avatar: formData.avatar.trim() || undefined,
      };

      await onSubmit(data);
      onClose();
    } catch (error) {
      if (error instanceof Error) {
        setErrors({ submit: error.message });
      } else {
        setErrors({ submit: 'An error occurred while saving the user' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={user ? 'Edit User' : 'Create New User'}
      size="medium"
    >
      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Name input */}
        <div className={styles.formGroup}>
          <label htmlFor="name" className={styles.label}>
            Name <span className={styles.required}>*</span>
          </label>
          <Input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Enter full name"
            error={errors.name}
            disabled={loading}
            glass
          />
          {errors.name && (
            <span className={styles.errorText}>{errors.name}</span>
          )}
        </div>

        {/* Email input */}
        <div className={styles.formGroup}>
          <label htmlFor="email" className={styles.label}>
            Email <span className={styles.required}>*</span>
          </label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="Enter email address"
            error={errors.email}
            disabled={loading}
            glass
          />
          {errors.email && (
            <span className={styles.errorText}>{errors.email}</span>
          )}
        </div>

        {/* Username input */}
        <div className={styles.formGroup}>
          <label htmlFor="username" className={styles.label}>
            Username
          </label>
          <Input
            id="username"
            type="text"
            value={formData.username}
            onChange={(e) => handleChange('username', e.target.value)}
            placeholder="Enter username"
            error={errors.username}
            disabled={loading}
            glass
            autoComplete="username"
          />
          {errors.username && (
            <span className={styles.errorText}>{errors.username}</span>
          )}
          <p className={styles.helpText}>
            Optional: 3-20 characters, letters, numbers, hyphens, and
            underscores only
          </p>
        </div>

        {/* Password input - only show when creating new user */}
        {!user && (
          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.label}>
              Password <span className={styles.required}>*</span>
            </label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              placeholder="Enter password"
              error={errors.password}
              disabled={loading}
              glass
              autoComplete="new-password"
            />
            {errors.password && (
              <span className={styles.errorText}>{errors.password}</span>
            )}
            <p className={styles.helpText}>
              Must be at least 8 characters and contain uppercase, lowercase,
              number, and special character
              (!@#$%^&*(),.?&quot;:&#123;&#125;|&lt;&gt;)
            </p>
          </div>
        )}

        {/* Role select */}
        <div className={styles.formGroup}>
          <label htmlFor="role" className={styles.label}>
            Role <span className={styles.required}>*</span>
          </label>
          <select
            id="role"
            value={formData.role}
            onChange={(e) =>
              handleChange('role', e.target.value as typeof formData.role)
            }
            className={styles.select}
            disabled={loading}
          >
            <option value={USER_ROLE_LOWERCASE.VIEWER}>Viewer</option>
            <option value={USER_ROLE_LOWERCASE.EDITOR}>Editor</option>
            <option value={USER_ROLE_LOWERCASE.ADMIN}>Admin</option>
            <option value={USER_ROLE_LOWERCASE.SUPER_ADMIN}>Super Admin</option>
          </select>
          <p className={styles.helpText}>
            {formData.role === USER_ROLE_LOWERCASE.SUPER_ADMIN &&
              ROLE_DESCRIPTIONS_LOWERCASE[USER_ROLE_LOWERCASE.SUPER_ADMIN]}
            {formData.role === USER_ROLE_LOWERCASE.ADMIN &&
              ROLE_DESCRIPTIONS_LOWERCASE[USER_ROLE_LOWERCASE.ADMIN]}
            {formData.role === USER_ROLE_LOWERCASE.EDITOR &&
              ROLE_DESCRIPTIONS_LOWERCASE[USER_ROLE_LOWERCASE.EDITOR]}
            {formData.role === USER_ROLE_LOWERCASE.VIEWER &&
              ROLE_DESCRIPTIONS_LOWERCASE[USER_ROLE_LOWERCASE.VIEWER]}
          </p>
        </div>

        {/* Status select */}
        <div className={styles.formGroup}>
          <label htmlFor="status" className={styles.label}>
            Status <span className={styles.required}>*</span>
          </label>
          <select
            id="status"
            value={formData.status}
            onChange={(e) =>
              handleChange('status', e.target.value as typeof formData.status)
            }
            className={styles.select}
            disabled={loading}
          >
            <option value={USER_STATUS_LOWERCASE.ACTIVE}>Active</option>
            <option value={USER_STATUS_LOWERCASE.INACTIVE}>Inactive</option>
            <option value={USER_STATUS_LOWERCASE.PENDING}>Pending</option>
            <option value={USER_STATUS_LOWERCASE.SUSPENDED}>Suspended</option>
          </select>
          <p className={styles.helpText}>
            {formData.status === USER_STATUS_LOWERCASE.ACTIVE &&
              'User can access the system'}
            {formData.status === USER_STATUS_LOWERCASE.INACTIVE &&
              'User access is disabled'}
            {formData.status === USER_STATUS_LOWERCASE.PENDING &&
              'User registration is pending'}
            {formData.status === USER_STATUS_LOWERCASE.SUSPENDED &&
              'User temporarily blocked from access'}
          </p>
        </div>

        {/* Avatar URL input */}
        <div className={styles.formGroup}>
          <label htmlFor="avatar" className={styles.label}>
            Avatar URL
          </label>
          <Input
            id="avatar"
            type="url"
            value={formData.avatar}
            onChange={(e) => handleChange('avatar', e.target.value)}
            placeholder="https://example.com/avatar.jpg"
            disabled={loading}
            glass
          />
          <p className={styles.helpText}>
            Optional: Enter a URL to display as the user&apos;s avatar
          </p>
        </div>

        {/* Submit error */}
        {errors.submit && (
          <div className={styles.submitError}>{errors.submit}</div>
        )}

        {/* Form actions */}
        <div className={styles.actions}>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
            glass
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading}
            glass
          >
            {user ? 'Update User' : 'Create User'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
